import {
  LAST_WORD_PADDING_SECONDS,
  MAXIMUM_CLUSTER_DISTANCE_SECONDS,
} from "@/constants";

export type TimeCluster = [number, number] | [number];

/**
 * Clusters a list of timestamps into groups based on their proximity in time.
 *
 * @param timestamps A list of numbers representing timestamps.
 * @param minDistance An optional parameter representing the minimum time distance
 *                    in seconds required for timestamps to be considered part of
 *                    different clusters. By default, `minDistance` is set to
 *                    `MAXIMUM_CLUSTER_DISTANCE_SECONDS`.
 *
 * @returns An array of `TimeCluster` arrays, where each `TimeCluster` array represents
 *          a group of timestamps that are close together in time.
 */
function clusterTimes(
  timestamps: number[],
  minDistance = MAXIMUM_CLUSTER_DISTANCE_SECONDS
): TimeCluster[] {
  if (timestamps.length === 0) {
    return [];
  }

  timestamps.sort((a, b) => a - b);

  const groupedTimestamps: TimeCluster[] = [];
  let groupStart = timestamps[0];

  for (let i = 1; i < timestamps.length; i++) {
    const prevTimestamp = timestamps[i - 1];
    const currentTimestamp = timestamps[i];

    if (currentTimestamp - prevTimestamp >= minDistance) {
      groupedTimestamps.push(
        groupStart === prevTimestamp
          ? [groupStart]
          : [groupStart, prevTimestamp]
      );
      groupStart = currentTimestamp;
    }
  }

  groupedTimestamps.push(
    groupStart === timestamps[timestamps.length - 1]
      ? [groupStart]
      : [
          groupStart,
          timestamps[timestamps.length - 1] + LAST_WORD_PADDING_SECONDS,
        ]
  );

  return groupedTimestamps;
}

export default clusterTimes;
